import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './SystemHealth.css';

const SystemHealth = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchHealth();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchHealth();
      }, 10000); // Refresh every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/monitoring/system-health');
      setHealth(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const getStatusColor = (status) => {
    if (status === 'healthy') return '#28a745';
    if (status === 'warning') return '#ffc107';
    return '#dc3545';
  };

  if (loading && !health) {
    return <div className="loading-message">Loading system health...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="system-health">
      <div className="health-controls">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (10s)
        </label>
        <button className="btn-refresh" onClick={fetchHealth}>
          🔄 Refresh
        </button>
      </div>

      <div className="health-grid">
        {/* Overall Status */}
        <div className="health-card status-card">
          <div className="card-header">
            <h3>System Status</h3>
          </div>
          <div className="card-body">
            <div
              className="status-indicator"
              style={{ backgroundColor: getStatusColor(health?.status) }}
            >
              {health?.status?.toUpperCase()}
            </div>
            {health?.health_issues && health.health_issues.length > 0 ? (
              <div className="status-summary warning">
                <strong>⚠️ Issues Detected:</strong>
                <ul>
                  {health.health_issues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="status-summary">
                ✓ All systems operational. CPU, Memory, Disk, and Database are within normal thresholds.
              </div>
            )}
            <div className="status-time">
              Last checked: {new Date(health?.timestamp).toLocaleString()}
            </div>
          </div>
        </div>

        {/* CPU Usage */}
        <div className="health-card">
          <div className="card-header">
            <h3>💻 CPU Usage</h3>
          </div>
          <div className="card-body">
            <div className="metric-value">{health?.cpu_percent}%</div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${health?.cpu_percent}%`,
                  backgroundColor: health?.cpu_percent > 80 ? '#dc3545' : '#28a745'
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Memory Usage */}
        <div className="health-card">
          <div className="card-header">
            <h3>🧠 Memory Usage</h3>
          </div>
          <div className="card-body">
            <div className="metric-value">{health?.memory_percent}%</div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${health?.memory_percent}%`,
                  backgroundColor: health?.memory_percent > 80 ? '#dc3545' : '#28a745'
                }}
              ></div>
            </div>
            <div className="metric-detail">
              {formatBytes(health?.memory_used)} / {formatBytes(health?.memory_total)}
            </div>
          </div>
        </div>

        {/* Disk Usage */}
        <div className="health-card">
          <div className="card-header">
            <h3>💾 Disk Usage</h3>
          </div>
          <div className="card-body">
            <div className="metric-value">{health?.disk_percent}%</div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${health?.disk_percent}%`,
                  backgroundColor: health?.disk_percent > 80 ? '#dc3545' : '#28a745'
                }}
              ></div>
            </div>
            <div className="metric-detail">
              {formatBytes(health?.disk_used)} / {formatBytes(health?.disk_total)}
            </div>
          </div>
        </div>

        {/* System Uptime */}
        <div className="health-card">
          <div className="card-header">
            <h3>⏱️ System Uptime</h3>
          </div>
          <div className="card-body">
            <div className="metric-value">{formatUptime(health?.uptime)}</div>
          </div>
        </div>

        {/* Database Status */}
        <div className="health-card">
          <div className="card-header">
            <h3>🗄️ Database</h3>
          </div>
          <div className="card-body">
            <div
              className="status-indicator small"
              style={{ backgroundColor: getStatusColor(health?.database_status) }}
            >
              {health?.database_status?.toUpperCase()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealth;
